import type { DocPage } from '../types'
import { media } from '../media'


export const workspace: DocPage = {
  id: 'workspace',
  title: 'Workspace tour',
  description:
    'A detailed tour of every major chrome area in the X-Translator workspace: header, toolbars, Source, Target, Mapping, vertical actions, and logger.',
  category: 'Workspace',
  sections: [
    { id: 'layout', title: 'Main layout' },
    { id: 'theme', title: 'Light and dark theme' },
    { id: 'header', title: 'Header' },
    { id: 'menu-bar', title: 'Menu bar' },
    { id: 'horizontal-toolbar', title: 'Horizontal toolbar' },
    { id: 'source', title: 'Source panel' },
    { id: 'target', title: 'Target panel' },
    { id: 'mapping', title: 'Mapping panel' },
    { id: 'vertical', title: 'Vertical action menu' },
    { id: 'logger', title: 'Bottom logger' },
    { id: 'overlays', title: 'Overlays and dialogs' },
  ],
  blocks: [
    {
      type: 'heading',
      level: 2,
      id: 'layout',
      text: 'Main layout',
    },
    {
      type: 'paragraph',
      text: 'X-Translator uses a single workspace rather than many separate pages. After sign-in you work in one composition: Source on the left, a vertical action rail in the middle, Target and Mapping on the right, and a resizable logger at the bottom. Header controls sit above everything for profiles, language, theme, and account.',
    },
    {
      type: 'figure',
      src: media.fullScreenLightMode,
      alt: 'X-Translator workspace overview',
      caption:
        'Figure: Full workspace in light theme — Source, vertical action rail, Target, Mapping, and logger.',
    },
    {
      type: 'table',
      headers: ['Area', 'Purpose'],
      rows: [
        ['Header', 'Logo, menu bar, Transfer Profiles, language, theme, profile menu'],
        ['Horizontal toolbar', 'Save Profile, Transfer History, related quick actions'],
        ['Source panel', 'Format, config, model tree or map, search, Feature Options, filters'],
        ['Vertical action menu', 'Connect, Connect-ID/Delete, Disconnect, Auto Map, Transfer, Clear'],
        ['Target panel', 'Target format, config, model tree or scratch destination'],
        ['Mapping panel', 'Collection/attribute mappings, F/V toolbar, import/export'],
        ['Bottom logger', 'LOGS / EVENTS / ERRORS tabs during and after transfer'],
      ],
    },
    {
      type: 'heading',
      level: 2,
      id: 'theme',
      text: 'Light and dark theme',
    },
    {
      type: 'paragraph',
      text: 'Use the theme toggle in the header to switch between light and dark appearance. Layout and tools stay the same; only colors change. Language (English / German) is also controlled from the header.',
    },
    {
      type: 'figure',
      src: media.fullScreenDarkMode,
      alt: 'X-Translator workspace in dark theme',
      caption:
        'Figure: Same workspace layout in dark theme — useful when comparing UI chrome for screenshots and training.',
    },
    {
      type: 'heading',
      level: 2,
      id: 'header',
      text: 'Header',
    },
    {
      type: 'list',
      items: [
        'Product logo / title identifying SEPM X-Translator.',
        'Transfer Profiles dropdown — load a saved source + target + mapping package.',
        'Language — switch English / German.',
        'Theme — light or dark appearance.',
        'Profile menu — name, email, picture, active organization, Organizations list, Refresh Account, Sign out.',
      ],
    },
    {
      type: 'heading',
      level: 2,
      id: 'menu-bar',
      text: 'Menu bar',
    },
    {
      type: 'paragraph',
      text: 'The menu bar exposes application-wide commands. Typical groups include Formats (format configuration / Default Settings), Transfer (Transfer Settings such as Coordinate Conversion), Logger (Global Log Settings, Log Configurations), and About.',
    },
    {
      type: 'table',
      headers: ['Menu area', 'Examples'],
      rows: [
        ['Formats', 'Default Settings, format configuration pathways'],
        ['Transfer', 'Coordinate Conversion (xy-coordinate X/Y)'],
        ['Logger', 'Global Log Settings, Log Configurations'],
        ['About', 'Version, copyright, legal notices'],
      ],
    },
    {
      type: 'heading',
      level: 2,
      id: 'horizontal-toolbar',
      text: 'Horizontal toolbar',
    },
    {
      type: 'list',
      items: [
        'Save Profile / Update Profile — persist the current transfer setup as a Transfer Profile.',
        'Unsaved indicators when the workspace differs from the loaded profile.',
        'Transfer History — open past runs (feature-gated by view-transfer-history).',
      ],
    },
    {
      type: 'heading',
      level: 2,
      id: 'source',
      text: 'Source panel',
    },
    {
      type: 'paragraph',
      text: 'Source is where you read data. Top to bottom you typically see:',
    },
    {
      type: 'list',
      ordered: true,
      items: [
        'Select Format and the Model Configurations dropdown (New, Create Custom Config, saved configs, Default badge).',
        'Connection summary card (host, file, ACE, EPSG, and similar fields depending on format).',
        'Tree / Map toggle when spatial overview is supported.',
        'Search for collections/attributes in the tree.',
        'Feature Options: Show Feature Counts, Hide Feature with 0 records, Show Collection Details.',
        'Add Filter / Change Filters for Collections and Attributes tabs.',
        'Model tree with selection for Connect / Auto Map scope.',
        'On Map: draw tools, inclusion mode, layers, basemap, attribute table entry points.',
      ],
    },
    {
      type: 'heading',
      level: 2,
      id: 'target',
      text: 'Target panel',
    },
    {
      type: 'paragraph',
      text: 'Target mirrors Source for format and configuration, but is oriented toward the write destination. For mapped transfers you load an existing target model tree and connect into it. For scratch transfers you configure an output path or catalog (Scratch Transfer Details) and X-Translator creates schema from the source.',
    },
    {
      type: 'list',
      items: [
        'Select Format → New or a saved Target configuration.',
        'Format-specific write options (Override Existing, style export accordion, scratch catalog fields).',
        'Model tree for mapped targets; scratch banner when no predefined target tree mapping is used.',
        'Search within the target tree when present.',
      ],
    },
    {
      type: 'heading',
      level: 2,
      id: 'mapping',
      text: 'Mapping panel',
    },
    {
      type: 'figure',
      src: media.mappingExample,
      alt: 'Mapping panel with toolbar',
      caption:
        'Figure: Mapping tree with Collection Mappings and attribute links (source → target).',
    },
    {
      type: 'paragraph',
      text: 'The Mapping panel lists connected collections and attributes. Toolbar actions include F (fixed mapping / Set fixed value), V (value mapping — mapped only), create-on-no-match key control, Import mapping, Export mapping, Remove inactive mapping, Clear mapping, and Search Mapping. Deep topics are covered under Mapping overview and related chapters.',
    },
    {
      type: 'heading',
      level: 2,
      id: 'vertical',
      text: 'Vertical action menu',
    },
    {
      type: 'figure',
      src: media.verticalMiddlePanel,
      alt: 'Vertical middle action button panel',
      caption:
        'Figure: Vertical action rail between Source and Target — Connect, Connect-ID, Connect-Delete, Disconnect, Transfer, Auto Map, and Clear.',
      size: 'rail',
    },
    {
      type: 'table',
      headers: ['Action', 'What it does'],
      rows: [
        ['Connect', 'Link selected matching collections or attributes'],
        [
          'Connect-ID',
          'Map attributes as update keys (match existing target rows)',
        ],
        [
          'Connect-Delete',
          'Map attributes as delete keys (remove matching target rows)',
        ],
        ['Disconnect', 'Remove the selected mapping link'],
        ['Auto Map', 'Open the Auto Map window for suggestions'],
        ['Smart Editor', 'Open advanced editing tools when available'],
        ['Transfer', 'Open Transfer Review and run the job'],
        ['Clear', 'Clear current mappings / selection context as offered'],
        ['Save Profile', 'Shortcut to save the transfer profile'],
      ],
    },
    {
      type: 'heading',
      level: 2,
      id: 'logger',
      text: 'Bottom logger',
    },
    {
      type: 'figure',
      src: media.currentTransferLogger,
      alt: 'Bottom transfer logger with LOGS EVENTS ERRORS',
      caption:
        'Figure: Bottom logger during/after a transfer — switch between LOGS, EVENTS, and ERRORS while monitoring progress.',
    },
    {
      type: 'paragraph',
      text: 'The logger has tabs for LOGS, EVENTS, and ERRORS. Resize, hide, or maximize it while a transfer runs. Pair it with Transfer History (downloadable logs) when diagnosing failures. Global Log Settings control level (NO LOG / STANDARD / DETAILED / DEBUG) when entitled.',
    },
    {
      type: 'heading',
      level: 2,
      id: 'overlays',
      text: 'Overlays and dialogs',
    },
    {
      type: 'list',
      items: [
        'Transfer Review modal before execution.',
        'Transfer progress overlay while the job runs.',
        'Access restricted dialogs for entitlement-gated settings.',
        'Organization switch / No access for current organization overlays.',
        'Auto Map as a separate window.',
        'Filters, logical object editors, Save Config, and profile dialogs.',
      ],
    },
  ],
}
